import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, LinearProgress, styled, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { isEqual, map } from 'lodash'
import { FC, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useRequestsStore } from '../../../../store/storeRequests'
import { theme } from '../../../../theme'
import ChevronLeftIcon from './../../../../assets/icons/ChevronLeft.svg?react'
import ChevronRightIcon from './../../../../assets/icons/ChevronRight.svg?react'
import MessageInfoCard from './MessageInfoCard'

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    padding: 0,
    height: '24px',
    width: '24px',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
        color: '#121317',
    },
}))

const messageInfoCards = [
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Ассистенты</Typography>
            </Box>
        }
        description="Более 20 готовых ИИ-экспертов: психолог, репетитор, фитнес-тренер и другие. Работают сразу и помогают без лишних настроек"
        imageSrc="/MessageInfoCards1.png"
    />,
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Автопилот</Typography>
            </Box>
        }
        description="Сам выбирает подходящую модель под твой запрос.  Всё работает автоматически, но при желании можно выбрать вручную"
        imageSrc="/MessageInfoCards2.png"
    />,
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Проекты</Typography>
            </Box>
        }
        description="Все твои запросы и файлы в одном месте. Контекст сохраняется, обновляется и помогает модели понимать над чем ты работаешь"
        imageSrc="/MessageInfoCards3.png"
    />,
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Контекст</Typography>
            </Box>
        }
        description="Модели запоминают важные детали и используют их при формировании ответов. Тебе не нужно повторяться при каждом новом запросе"
        imageSrc="/MessageInfoCards4.png"
    />,
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Защищённый проект</Typography>
            </Box>
        }
        description="Все данные хранятся только на серверах Arhitex в России  и обрабатываются защищёнными моделями. Это обеспечивает полную конфиденциальность"
        imageSrc="/MessageInfoCards5.png"
    />,
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Встречи</Typography>
            </Box>
        }
        description="Бот подключается к встречам, записывает разговор и создаёт расшифровку вместе с саммари. Всё нужное остаётся под рукой без переслушивания"
        imageSrc="/MessageInfoCards6.png"
    />,
    <MessageInfoCard
        title={
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                }}>
                <Typography variant="h6">Календарь</Typography>
            </Box>
        }
        description="Календарь показывает запланированные встречи и регулярные запросы. Всё в одном месте, чтобы удобно планировать работу и держать порядок"
        imageSrc="/MessageInfoCards7.png"
    />,
]

const MessageInfoBlock: FC = () => {
    const [activeCard, setActiveCard] = useState(0)
    const params = useParams()
    const [progress, setProgress] = useState(0)
    const { requestDetail, updateRequest } = useRequestsStore((state) => state)
    const [isClosed, setIsClosed] = useState(false)
    const timerRef = useRef(null)
    const { mutate: updateThread } = useMutation({
        mutationFn: () =>
            updateRequest({ threadId: +params.requestId }, { disable_info_messages: true }),
    })

    const getUpdateInterval = (duration: number) => duration / 100

    useEffect(() => {
        const duration = 10000
        const interval = getUpdateInterval(duration)

        timerRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    goToNext()
                    return 0
                }
                return prev + 1
            })
        }, interval)

        return () => clearInterval(timerRef.current)
    }, [activeCard])

    const goToNext = () => {
        setActiveCard((prev) => (prev + 1) % messageInfoCards.length)
        setProgress(0)
    }

    const goToPrev = () => {
        setActiveCard((prev) => (prev - 1 + messageInfoCards.length) % messageInfoCards.length)
        setProgress(0)
    }

    if (isClosed || requestDetail?.disable_info_messages) {
        return null
    }

    return (
        <Box
            sx={{
                background: '#F5F9FB',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                border: '1px solid #E9EAED',
                borderRadius: '24px',
                padding: '16px',
                gap: '8px',
                [theme.breakpoints.down('md')]: {
                    width: '100%',
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '3px',
                    height: '24px',
                }}>
                {map(messageInfoCards, (_, index) => (
                    <LinearProgress
                        key={index}
                        variant="determinate"
                        value={activeCard === index ? progress : index < activeCard ? 100 : 0}
                        sx={{
                            height: '4px',
                            borderRadius: '24px',
                            flex: 1,
                        }}
                    />
                ))}
                <IconButton
                    onClick={() => {
                        updateThread()
                        setIsClosed(true)
                    }}
                    size="small">
                    <CloseIcon />
                </IconButton>
            </Box>
            {messageInfoCards[activeCard]}
            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }}>
                <StyledIconButton onClick={goToPrev} disabled={isEqual(activeCard, 0)}>
                    <ChevronLeftIcon />
                </StyledIconButton>
                <StyledIconButton
                    onClick={goToNext}
                    disabled={isEqual(activeCard, messageInfoCards.length - 1)}>
                    <ChevronRightIcon />
                </StyledIconButton>
            </Box>
        </Box>
    )
}

export default MessageInfoBlock
